import type { ResourcesConfig } from 'aws-amplify';

const getRequiredEnv = (name: string): string => {
  const value = process.env[name];
  if (!value) {
    throw new Error(`Missing required build-time env var: ${name}`);
  }
  return value;
};

type TenantUserPoolEnvNames = {
  userPoolIdEnv: string;
  userPoolClientIdEnv: string;
};

type TenantUserPoolConfig = {
  readonly userPoolId: string;
  readonly userPoolClientId: string;
};

const TENANT_USER_POOL_ENV_NAMES: Record<string, TenantUserPoolEnvNames> = {
  homebrew: {
    userPoolIdEnv: 'REACT_APP_HOMEBREW_USER_POOL_ID',
    userPoolClientIdEnv: 'REACT_APP_HOMEBREW_USER_POOL_WEB_CLIENT_ID',
  },
  cpf: {
    userPoolIdEnv: 'REACT_APP_CPF_USER_POOL_ID',
    userPoolClientIdEnv: 'REACT_APP_CPF_USER_POOL_WEB_CLIENT_ID',
  },
  core: {
    userPoolIdEnv: 'REACT_APP_CORE_USER_POOL_ID',
    userPoolClientIdEnv: 'REACT_APP_CORE_USER_POOL_WEB_CLIENT_ID',
  },
  csc: {
    userPoolIdEnv: 'REACT_APP_CSC_USER_POOL_ID',
    userPoolClientIdEnv: 'REACT_APP_CSC_USER_POOL_WEB_CLIENT_ID',
  },
  psfa: {
    userPoolIdEnv: 'REACT_APP_PSFA_USER_POOL_ID',
    userPoolClientIdEnv: 'REACT_APP_PSFA_USER_POOL_WEB_CLIENT_ID',
  },
  scs: {
    userPoolIdEnv: 'REACT_APP_SCS_USER_POOL_ID',
    userPoolClientIdEnv: 'REACT_APP_SCS_USER_POOL_WEB_CLIENT_ID',
  },
  gdad: {
    userPoolIdEnv: 'REACT_APP_GDAD_USER_POOL_ID',
    userPoolClientIdEnv: 'REACT_APP_GDAD_USER_POOL_WEB_CLIENT_ID',
  },
  reward: {
    userPoolIdEnv: 'REACT_APP_REWARD_USER_POOL_ID',
    userPoolClientIdEnv: 'REACT_APP_REWARD_USER_POOL_WEB_CLIENT_ID',
  },

  disposal: {
    userPoolIdEnv: 'REACT_APP_DISPOSAL_USER_POOL_ID',
    userPoolClientIdEnv: 'REACT_APP_DISPOSAL_USER_POOL_WEB_CLIENT_ID',
  },
  rpalogin: {
    userPoolIdEnv: 'REACT_APP_RPALOGIN_USER_POOL_ID',
    userPoolClientIdEnv: 'REACT_APP_RPALOGIN_USER_POOL_WEB_CLIENT_ID',
  },
  dcat: {
    userPoolIdEnv: 'REACT_APP_DCAT_USER_POOL_ID',
    userPoolClientIdEnv: 'REACT_APP_DCAT_USER_POOL_WEB_CLIENT_ID',
  },
  tableau: {
    userPoolIdEnv: 'REACT_APP_TABLEAU_USER_POOL_ID',
    userPoolClientIdEnv: 'REACT_APP_TABLEAU_USER_POOL_WEB_CLIENT_ID',
  },
  invite: {
    userPoolIdEnv: 'REACT_APP_INVITE_USER_POOL_ID',
    userPoolClientIdEnv: 'REACT_APP_INVITE_USER_POOL_WEB_CLIENT_ID',
  },

  bedrock: {
    userPoolIdEnv: 'REACT_APP_BEDROCK_USER_POOL_ID',
    userPoolClientIdEnv: 'REACT_APP_BEDROCK_USER_POOL_WEB_CLIENT_ID',
  },

  gfie: {
    userPoolIdEnv: 'REACT_APP_GFIE_USER_POOL_ID',
    userPoolClientIdEnv: 'REACT_APP_GFIE_USER_POOL_WEB_CLIENT_ID',
  },
  secondments: {
    userPoolIdEnv: 'REACT_APP_SECONDMENTS_USER_POOL_ID',
    userPoolClientIdEnv: 'REACT_APP_SECONDMENTS_USER_POOL_WEB_CLIENT_ID',
  },
};

export const getTenantAuthRealm = (tenant: string) => tenant;

const createTenantUserPoolConfig = ({
  userPoolIdEnv,
  userPoolClientIdEnv,
}: TenantUserPoolEnvNames): TenantUserPoolConfig => ({
  get userPoolId() {
    return getRequiredEnv(userPoolIdEnv);
  },
  get userPoolClientId() {
    return getRequiredEnv(userPoolClientIdEnv);
  },
});

// Tenant-specific user pool configurations are resolved lazily so scaffolded tenants
// do not break unrelated builds before their infrastructure exists.
export const TENANT_USER_POOL_CONFIGS: Record<string, TenantUserPoolConfig> = Object.fromEntries(
  Object.entries(TENANT_USER_POOL_ENV_NAMES).map(([tenant, envNames]) => [
    tenant,
    createTenantUserPoolConfig(envNames),
  ]),
);

// Default bootstrap configuration
export const DEFAULT_AUTH_CONFIG: ResourcesConfig = {
  Auth: {
    Cognito: {
      userPoolId: TENANT_USER_POOL_CONFIGS.core.userPoolId,
      userPoolClientId: TENANT_USER_POOL_CONFIGS.core.userPoolClientId,
    },
  },
};
